<template>
	<section class="hour-table-container" :dir="tableDir">
		<div class="table-header">
			<h3 class="table-title">{{ $t('data_by_hour') }}</h3>
			<div class="table-controls">
				<span v-if="showAverages" class="view-note">{{ avgArrivalsLabel }}</span>
				<div v-if="canToggleDailyAverage" class="view-toggle" role="group" :aria-label="avgArrivalsLabel">
					<button
						type="button"
						class="toggle-btn"
						:class="{ active: !showAverages }"
						:aria-pressed="!showAverages"
						@click="showAverages = false"
					>
						{{ $t('hour_values_total') }}
					</button>
					<button
						type="button"
						class="toggle-btn"
						:class="{ active: showAverages }"
						:aria-pressed="showAverages"
						:title="avgArrivalsLabel"
						@click="showAverages = true"
					>
						{{ $t('hour_values_per_day') }}
					</button>
				</div>
				<button class="control-btn" @click="toggleCompactView" :title="$t('toggle_compact_view')">
					<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
						<path d="M3 4h18v2H3V4zm0 7h18v2H3v-2zm0 7h18v2H3v-2z" />
					</svg>
				</button>
			</div>
		</div>

		<div class="table-wrapper" :class="{ compact: isCompactView }">
			<table class="hour-table" role="table">
				<thead>
					<tr role="row">
						<th class="table-head-cell metric-column" scope="col" role="columnheader">
							<div class="metric-header">
								<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="clock-icon">
									<path
										d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"
									/>
								</svg>
								<span class="sr-only">{{ $t('time_hours') }}</span>
							</div>
						</th>
						<th
							v-for="item in dataByHour"
							:key="item.time"
							class="table-head-cell hour-column"
							:class="{ highlight: currentlyFilteringHour === item.time, clickable: true }"
							@click="$emit('hourClick', item.time)"
							@keydown.enter="$emit('hourClick', item.time)"
							@keydown.space.prevent="$emit('hourClick', item.time)"
							:tabindex="0"
							role="columnheader button"
							:aria-label="`${$t('hour')} ${numberToHour(item.time)}, ${$t('click_to_filter')}`"
						>
							<span class="hour-text">{{ numberToHour(item.time) }}</span>
						</th>
					</tr>
				</thead>

				<tbody>
					<!-- Complaints Row -->
					<tr v-if="isShown.cleanComplaint" class="data-row complaints-row" role="row">
						<td class="metric-cell" role="rowheader">
							{{ inSurveyOverviewPage ? $t('clicks') : '' }}
							<img
								v-if="!inSurveyOverviewPage"
								class="metric-icon"
								:src="require('@/assets/performance/icons-complaint.svg')"
								alt="complaints"
								:title="$t('C.complaints')"
							/>
						</td>
						<td
							v-for="item in dataByHour"
							:key="`complaints-${item.time}`"
							class="data-cell"
							:style="{
								'--intensity': item.complaints / dataByHourEachMax.complaints,
								backgroundColor: `hsla(345, 45%, 75%, ${
									(item.complaints / dataByHourEachMax.complaints) * 0.8
								})`
							}"
							role="cell"
							:aria-label="hourAria($t('complaints'), item, item.complaints)"
						>
							<div class="cell-content">
								<span class="cell-value">{{ hourCellText(item, item.complaints) }}</span>
							</div>
						</td>
					</tr>

					<!-- Arrivals Row -->
					<tr v-if="isShown.arrivals" class="data-row arrivals-row" role="row">
						<td class="metric-cell" role="rowheader">
							{{ inSurveyOverviewPage ? $t('cleaner_arrivals') : '' }}
							<img
								v-if="!inSurveyOverviewPage"
								class="metric-icon"
								:src="require('@/assets/performance/cleaner_arrival-icon.svg')"
								alt="cleaning arrivals"
								:title="$t('C.arrivals')"
							/>
						</td>
						<td
							v-for="item in dataByHour"
							:key="`arrivals-${item.time}`"
							class="data-cell"
							:style="{
								'--intensity': item.arrivals / dataByHourEachMax.arrivals,
								backgroundColor: `hsla(100, 35%, 75%, ${(item.arrivals / dataByHourEachMax.arrivals) * 0.8})`
							}"
							role="cell"
							:aria-label="hourAria($t('cleaner_arrivals'), item, item.arrivals)"
						>
							<div class="cell-content">
								<span class="cell-value">{{ hourCellText(item, item.arrivals) }}</span>
								<div v-if="item.originalArrivals != null && item.originalArrivals !== item.arrivals && isAmendedPermitted" class="amended-indicator">
									<span class="amended-icon">*</span>
									<div class="amended-tooltip">
										{{ $t('original') }}: {{ hourCellText(item, item.originalArrivals) }}
									</div>
								</div>
							</div>
						</td>
					</tr>

					<tr v-if="isShown.visits" class="data-row visits-row" role="row">
						<td class="metric-cell" role="rowheader">
							<img
								class="metric-icon"
								:src="require('@/assets/performance/icons-visits.svg')"
								alt="visits"
								:title="$t('visits')"
							/>
						</td>
						<td
							v-for="item in dataByHour"
							:key="`visits-${item.time}`"
							class="data-cell"
							:style="{
								'--intensity': item.visits / dataByHourEachMax.visits,
								backgroundColor: `hsla(310, 25%, 85%, ${(item.visits / dataByHourEachMax.visits) * 0.8})`
							}"
							role="cell"
							:aria-label="hourAria($t('visits'), item, item.visits)"
						>
							<div class="cell-content">
								<span class="cell-value">{{ hourCellText(item, item.visits) }}</span>
								<div v-if="item.isAmended && item.originalVisits != null && Number(item.visits) !== Number(item.originalVisits) && isAmendedPermitted" class="amended-indicator">
									<span class="amended-icon">*</span>
									<div class="amended-tooltip">
										{{ $t('original') }}: {{ hourCellText(item, item.originalVisits) }}
									</div>
								</div>
							</div>
						</td>
					</tr>

					<!-- Maintenance Complaints Row -->
					<tr v-if="isShown.maintenanceComplaint" class="data-row maintenance-complaints-row" role="row">
						<td class="metric-cell" role="rowheader">
							<img
								class="metric-icon"
								:src="require('@/assets/performance/icons-complaint_blue.svg')"
								alt="maintenance complaints"
								:title="$t('M.complaints')"
							/>
						</td>
						<td
							v-for="item in dataByHour"
							:key="`maintenance-complaints-${item.time}`"
							class="data-cell"
							:style="{
								'--intensity': item.maintenanceComplaint / dataByHourEachMax.maintenanceComplaint,
								backgroundColor: `hsla(25, 45%, 75%, ${
									(item.maintenanceComplaint / dataByHourEachMax.maintenanceComplaint) * 0.8
								})`
							}"
							role="cell"
							:aria-label="hourAria($t('M.complaints'), item, item[dataKey('maintenanceComplaint')])"
						>
							<div class="cell-content">
								<span class="cell-value">{{ hourCellText(item, item[dataKey('maintenanceComplaint')]) }}</span>
							</div>
						</td>
					</tr>

					<!-- Maintenance Arrivals Row -->
					<tr v-if="isShown.maintenanceArrivals" class="data-row maintenance-arrivals-row" role="row">
						<td class="metric-cell" role="rowheader">
							<img
								class="metric-icon"
								:src="require('@/assets/performance/maintenance-arrivals_purple.svg')"
								alt="maintenance arrivals"
								:title="$t('M.Arrivals')"
							/>
						</td>
						<td
							v-for="item in dataByHour"
							:key="`maintenance-arrivals-${item.time}`"
							class="data-cell"
							:style="{
								'--intensity': item.maintenanceArrivals / dataByHourEachMax.maintenanceArrivals,
								backgroundColor: `hsla(210, 55%, 75%, ${
									(item.maintenanceArrivals / dataByHourEachMax.maintenanceArrivals) * 0.8
								})`
							}"
							role="cell"
							:aria-label="hourAria($t('M.Arrivals'), item, item[dataKey('maintenanceArrivals')])"
						>
							<div class="cell-content">
								<span class="cell-value">{{ hourCellText(item, item[dataKey('maintenanceArrivals')]) }}</span>
							</div>
						</td>
					</tr>

					<!-- Likes Row (last row, shown only when there is likes data) -->
					<tr v-if="isShown.likes" class="data-row likes-row" role="row">
						<td class="metric-cell" role="rowheader">
							<span
								class="metric-icon metric-icon-heart"
								role="img"
								aria-label="likes"
								:title="$t('likes')"
							>💚</span>
						</td>
						<td
							v-for="item in dataByHour"
							:key="`likes-${item.time}`"
							class="data-cell"
							:style="{
								'--intensity': (item.likes || 0) / (dataByHourEachMax.likes || 1),
								backgroundColor: `hsla(140, 40%, 75%, ${((item.likes || 0) / (dataByHourEachMax.likes || 1)) * 0.8})`
							}"
							role="cell"
							:aria-label="hourAria($t('likes'), item, item.likes || 0)"
						>
							<div class="cell-content">
								<span class="cell-value">{{ hourCellText(item, item.likes || 0) }}</span>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<!-- Enhanced Legend with Better Visual Design -->
		<section class="legend-container" role="region" aria-label="Data legend">
			<div class="legend-grid">
				<div v-if="isShown.cleanComplaint" class="legend-item">
					<img :src="require('@/assets/performance/icons-complaint.svg')" alt="complaints" class="legend-icon" />
					<span class="legend-label">{{ $t('C.complaints') }}</span>
				</div>
				<div v-if="isShown.arrivals" class="legend-item">
					<img
						:src="require('@/assets/performance/cleaner_arrival-icon.svg')"
						alt="cleaning arrivals"
						class="legend-icon"
					/>
					<span class="legend-label">{{ $t('C.arrivals') }}</span>
				</div>
				<div v-if="isShown.visits" class="legend-item">
					<img :src="require('@/assets/performance/icons-visits.svg')" alt="visits" class="legend-icon" />
					<span class="legend-label">{{ $t('visits') }}</span>
				</div>
				<div v-if="isShown.maintenanceComplaint" class="legend-item">
					<img
						:src="require('@/assets/performance/icons-complaint_blue.svg')"
						alt="maintenance complaints"
						class="legend-icon"
					/>
					<span class="legend-label">{{ $t('M.complaints') }}</span>
				</div>
				<div v-if="isShown.maintenanceArrivals" class="legend-item">
					<img
						:src="require('@/assets/performance/maintenance-arrivals_purple.svg')"
						alt="maintenance arrivals"
						class="legend-icon"
					/>
					<span class="legend-label">{{ $t('M.Arrivals') }}</span>
				</div>
				<div v-if="isShown.likes" class="legend-item">
					<span class="legend-icon metric-icon-heart" role="img" aria-label="likes">💚</span>
					<span class="legend-label">{{ $t('likes') }}</span>
				</div>
			</div>
		</section>
	</section>
</template>

<script>
import util from '@/modules/common/services/utilService'
import { byHourTableConst } from '../consts/consts'
import { mapGetters } from 'vuex'

export default {
	props: {
		dataByHour: { type: Array, required: true },
		hideRows: { type: Array, required: false, default: () => [] },
		inSurveyOverviewPage: { type: Boolean, required: false, default: false },
		isExporting: { type: Boolean, required: false, default: false },
	},
	data() {
		return {
			descriptionConst: byHourTableConst,
			isCompactView: false,
			showAverages: false
		}
	},
	mounted() {
		this.$emit('mounted')
	},
	computed: {
		...mapGetters(['loggedUser', 'isRTL']),
		currentlyFilteringHour() {
			const filter = this.$store.getters.filterSelected
			const dateToCalculateLocalHour = new Date()
			dateToCalculateLocalHour.setHours(filter.hour)
			const currFilteringHour = dateToCalculateLocalHour.getHours()
			return currFilteringHour || null
		},
		isShown() {
			return {
				visits: this.isRowData('visits'),
				likes: this.isRowData('likes'),
				cleanComplaint: this.isRowData('complaints') || !!(this.dataByHour[0].cleaningProblemsByServik + 1),
				arrivals: this.isRowData('arrivals'),
				maintenanceComplaint:
					this.isRowData('maintenanceComplaint') || !!(this.dataByHour[0].maintenanceProblemsByServik + 1),
				maintenanceArrivals: this.isRowData('maintenanceArrivals')
			}
		},
		dataByHourEachMax() {
			return this.dataByHour.reduce((maxMap, item) => {
				for (const key in item) {
					if (!maxMap[key] || maxMap[key] < item[key]) maxMap[key] = item[key]
				}
				return maxMap
			}, {})
		},
		canToggleDailyAverage() {
			const count = this.dataByHour[0] && Number(this.dataByHour[0].workingDayCount)
			return Number.isFinite(count) && count > 0
		},
		avgArrivalsLabel() {
			const count = this.dataByHour[0] && this.dataByHour[0].workingDayCount
			if (!count) return this.$t('per_working_day')
			return this.$t('per_working_day_count', { count })
		},
		isCleanDrillDown() {
			return this.dataByHour.some((a) => a.cleaningProblemsByServik)
		},
		isMaintenanceDrillDown() {
			return this.dataByHour.some((a) => a.maintenanceProblemsByServik)
		},
		isAmendedPermitted() {
			const { level } = this.loggedUser
			return level === 'buzzz'
		},
		tableDir() {
			if (!this.isExporting) return undefined
			return this.isRTL ? 'rtl' : 'ltr'
		},
	},
	methods: {
		kFormatter(number) {
			if (number === 0) return ''
			return util.kFormatter(number)
		},
		formatAvgArrivals(value) {
			const amount = Number(value) || 0
			if (amount <= 0) return ''
			if (amount < 1) return amount.toFixed(2)
			const rounded = Math.round(amount * 10) / 10
			return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
		},
		hourCellText(item, rawValue) {
			const amount = Number(rawValue) || 0
			if (!this.showAverages) return this.kFormatter(amount)
			const days = Number(item && item.workingDayCount) || 0
			if (days <= 0) return this.kFormatter(amount)
			return this.formatAvgArrivals(amount / days)
		},
		hourAria(label, item, rawValue) {
			const shown = this.hourCellText(item, rawValue) || 0
			return `${label}: ${shown} ${this.$t('at_hour')} ${this.numberToHour(item.time)}`
		},
		isRowData(field) {
			if (this.hideRows.length && this.hideRows.includes(field)) return false
			const isDataForRow = this.dataByHour.some((item) => item[field] > 0)
			return isDataForRow
		},
		dataKey(name) {
			if (!this.isMaintenanceDrillDown && !this.isCleanDrillDown) return name
			else if (name === 'maintenanceComplaint' && this.isMaintenanceDrillDown) return 'maintenanceProblemsByServik'
			else if (name === 'cleanComplaint' && this.isCleanDrillDown) return 'cleaningProblemsByServik'
		},
		toggleCompactView() {
			this.isCompactView = !this.isCompactView
		},
		numberToHour(number) {
			return number >= 10 ? `${number}:00` : `0${number}:00`;
		}
	}
}
</script>

<style lang="scss" scoped>
@use '@/styles/mixins' as *;

.hour-table-container {
	background: $white;
	border-radius: 12px;
	@include shadow-border;
	overflow: hidden;

	&[dir='rtl'] {
		.hour-table {
			direction: rtl;
		}

		.table-title {
			text-align: start;
		}

		.legend-grid {
			direction: rtl;
		}

		.legend-label {
			text-align: start;
		}
	}
}

.table-header {
	@include flex-space-between;
	padding: 0.75rem 1rem;
	background: linear-gradient(135deg, $light-grey 0%, darken($light-grey, 3%) 100%);
	@include border($grey-border-color);
	border-bottom: 1px solid $grey-border-color;

	.table-title {
		margin: 0;
		font-size: $font-size-regular;
		font-weight: $font-weight-semi-bold;
		@include small-titles;
	}

	.table-controls {
		display: flex;
		align-items: center;
		gap: 0.5rem;

		.view-note {
			color: $dark-grey;
			font-size: 11px;
			line-height: 1.2;
			white-space: nowrap;
		}

		.view-toggle {
			display: inline-flex;
			height: 28px;
			background: $white;
			border-radius: 4px;
			overflow: hidden;
			@include border($grey-border-color);
		}

		.toggle-btn {
			border: none;
			background: transparent;
			padding: 0 0.65rem;
			font-family: inherit;
			font-size: 12px;
			line-height: 28px;
			color: $dark-grey;
			cursor: pointer;

			& + .toggle-btn {
				border-inline-start: 1px solid $grey-border-color;
			}

			&.active {
				background: $blue;
				color: $white;
			}

			&:focus {
				outline: 2px solid $blue;
				outline-offset: -2px;
			}
		}

		.control-btn {
			@include flex-center;
			width: 28px;
			height: 28px;
			@include border($grey-border-color);
			background: $white;
			border-radius: 4px;
			cursor: pointer;
			color: $dark-grey;
			transition: all 0.2s ease;

			&:hover {
				background: lighten($light-grey, 2%);
				color: $blue;
				border-color: $blue;
			}

			&:focus {
				outline: 2px solid $blue;
				outline-offset: 2px;
			}
		}
	}
}

.table-wrapper {
	overflow-x: auto;
	max-width: 100%;

	&.compact {
		.hour-table {
			font-size: 0.75rem;

			.data-cell {
				padding: 0.25rem;
			}
		}
	}
}

.hour-table {
	width: 100%;
	border-collapse: collapse;
	font-size: $font-size-small;
	line-height: 1.25rem;

	// Header Styles
	thead {
		background: $light-grey;

		tr {
			border-bottom: 2px solid $grey-border-color;
		}

		.table-head-cell {
			padding: 0.5rem 0.25rem;
			text-align: center;
			font-weight: $font-weight-semi-bold;
			color: $black-blue;
			@include border($grey-border-color);
			border-left: none;
			border-top: none;
			position: relative;

			&.metric-column {
				width: 60px;
				min-width: 60px;
				background: linear-gradient(135deg, $light-grey 0%, darken($light-grey, 5%) 100%);
			}

			&.hour-column {
				min-width: 52px;
				cursor: pointer;
				transition: all 0.2s ease;
				user-select: none;

				&:hover {
					background: lighten($light-grey, 3%);
				}

				&:focus {
					outline: 2px solid $blue;
					outline-offset: -2px;
				}

				&.highlight {
					background: $blue;
					color: $white;
					font-weight: $font-weight-bold;
				}

				.hour-text {
					display: block;
					padding: 0.25rem;
					border-radius: 4px;
				}
			}

			&:last-child {
				border-right: none;
			}
		}
	}

	// Body Styles
	tbody {
		.data-row {
			border-bottom: 1px solid $grey-border-color;
			transition: background-color 0.2s ease;

			&:hover {
				background: rgba($blue, 0.02);
			}

			&:last-child {
				border-bottom: none;
			}
		}

		.metric-cell {
			padding: 0.5rem 0.25rem;
			background: $light-grey;
			@include border($grey-border-color);
			border-left: none;
			border-top: none;
			border-bottom: none;
			width: 60px;
			min-width: 60px;
			vertical-align: middle;
			font-size: $font-size-smaller;
			text-align: center;

			.metric-icon {
				width: 18px;
				height: 18px;
				display: inline-block;
				vertical-align: middle;
				cursor: default;
			}

			.metric-icon-heart {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				font-size: 18px;
				line-height: 1;
			}
		}

		.data-cell {
			padding: 0.375rem 0.125rem;
			text-align: center;
			@include border($grey-border-color);
			border-left: none;
			border-top: none;
			border-bottom: none;
			vertical-align: middle;
			position: relative;
			transition: all 0.2s ease;
			min-height: 2rem;

			&:hover {
				background: rgba($blue, 0.05);
			}

			&:last-child {
				border-right: none;
			}

			.cell-content {
				@include flex-center;
				gap: 0.25rem;
				position: relative;
			}

			.cell-value {
				font-weight: $font-weight-regular;
				color: $black-blue;
				font-size: $font-size-smaller;
			}
		}
	}
}

.amended-indicator {
	position: relative;
	display: inline-flex;
	align-items: center;

	.amended-icon {
		color: $blue;
		font-weight: $font-weight-bold;
		font-size: 1rem;
		cursor: help;
	}

	.amended-tooltip {
		position: absolute;
		bottom: 100%;
		left: 50%;
		transform: translateX(-50%);
		background: $black-blue;
		color: $white;
		padding: 0.5rem 0.75rem;
		border-radius: 6px;
		font-size: $font-size-smaller;
		white-space: nowrap;
		opacity: 0;
		visibility: hidden;
		transition: all 0.2s ease;
		z-index: 10;
		margin-bottom: 0.5rem;

		&::after {
			content: '';
			position: absolute;
			top: 100%;
			left: 50%;
			transform: translateX(-50%);
			border: 4px solid transparent;
			border-top-color: $black-blue;
		}
	}

	&:hover .amended-tooltip {
		opacity: 1;
		visibility: visible;
	}
}

.legend-container {
	padding: 0.75rem 1rem;
	background: $light-grey;
	@include border($grey-border-color);
	border-bottom: none;
	border-left: none;
	border-right: none;

	.legend-title {
		margin: 0 0 0.5rem 0;
		font-size: $font-size-smaller;
		font-weight: $font-weight-semi-bold;
		color: $black-blue;
		text-transform: uppercase;
		letter-spacing: 0.05em;
	}

	.legend-grid {
		@include flex-wrap;
		gap: 0.75rem;
	}

	.legend-item {
		@include align-center;
		gap: 0.375rem;
		font-size: $font-size-smaller;

		.legend-icon {
			width: 14px;
			height: 14px;
			flex-shrink: 0;
		}

		.legend-icon.metric-icon-heart {
			width: auto;
			height: auto;
			font-size: 14px;
			line-height: 1;
		}

		.legend-label {
			color: $black-blue;
			font-weight: $font-weight-regular;
		}
	}
}

.sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}

@include screen-size(md) {
	.table-header {
		padding: 1rem;

		.table-title {
			font-size: $font-size-medium;
		}
	}

	.hour-table {
		font-size: $font-size-smaller;

		.metric-cell {
			width: 100px;
			min-width: 100px;
			padding: 0.75rem 0.5rem;

			.metric-info {
				flex-direction: column;
				gap: 0.25rem;
				text-align: center;
			}

			.metric-icon {
				width: 24px;
				height: 24px;
			}

			.metric-icon-heart {
				font-size: 24px;
			}

			.metric-label {
				font-size: $font-size-smaller;
			}
		}

		.data-cell {
			padding: 0.5rem 0.25rem;
		}
	}

	.legend-container {
		padding: 1rem;

		.legend-grid {
			gap: 1rem;
		}
	}
}

@include screen-size(xs) {
	.table-header {
		padding: 0.75rem;
		flex-direction: column;
		gap: 1rem;

		.table-title {
			font-size: $font-size-regular;
		}
	}

	.hour-table {
		font-size: $font-size-tiny;

		.metric-cell {
			width: 80px;
			padding: 0.5rem 0.25rem;

			.metric-label {
				font-size: $font-size-tiny;
			}
		}

		.data-cell {
			padding: 0.375rem 0.125rem;
		}
	}

	.legend-container {
		padding: 0.75rem;

		.legend-grid {
			gap: 0.75rem;
		}

		.legend-item {
			font-size: $font-size-smaller;
		}
	}
}
</style>
